/**
 * User Management Service
 * 
 * Handles user creation, deletion, and synchronization to devices via MQTT.
 * Integrates with AWS Cognito for user management and AWS IoT Core for device communication.
 */

import { Buffer } from 'buffer';
import { 
  CognitoIdentityProviderClient,
  AdminCreateUserCommand,
  AdminDeleteUserCommand,
  AdminAddUserToGroupCommand,
  AdminSetUserPasswordCommand,
  MessageActionType
} from '@aws-sdk/client-cognito-identity-provider'
import { 
  IoTDataPlaneClient,
  PublishCommand
} from '@aws-sdk/client-iot-data-plane'
import { fetchAuthSession } from 'aws-amplify/auth'
import { generateClient } from 'aws-amplify/data'
import { type Schema } from '../../amplify/data/resource'

const dataClient = generateClient<Schema>()

/**
 * Request interface for creating a new user
 */
export interface CreateUserRequest {
  username: string
  email: string
  temporaryPassword: string
  cognitoGroup: 'superAdmin' | 'groupAdmin' | 'groupUser'
  parentGroup: string
  deviceIds?: string[] // Optional: specific devices to sync to
}

/**
 * User data structure for MQTT synchronization
 */
export interface UserData {
  username: string
  email: string
  passwordHash: string
  cognitoGroup: string
  parentGroup: string
  isActive: boolean
}

/**
 * MQTT sync message types
 */
export type SyncMessageType = 'FullSync' | 'IncrementalSync' | 'UserDeleted'

/**
 * MQTT user sync message structure
 */
export interface UserSyncMessage {
  messageType: SyncMessageType
  users: UserData[]
  timestamp: string
}

/**
 * Device information from database
 */
interface Device {
  id: string
  deviceId: string
  userId: string
  iotThingName?: string
}

/**
 * User Management Service Class
 * 
 * Provides methods for managing users in Cognito and synchronizing them to devices.
 */
class UserManagementService {
  private cognitoClient: CognitoIdentityProviderClient
  private iotClient: IoTDataPlaneClient
  private userPoolId: string
  private region: string

  constructor() {
    // These will be properly initialized in the initialize() method
    this.region = 'ap-southeast-2' // Default, will be overridden
    this.userPoolId = ''
    
    // Initialize with default region (will be updated in initialize())
    this.cognitoClient = new CognitoIdentityProviderClient({ region: this.region })
    this.iotClient = new IoTDataPlaneClient({ region: this.region })
  }

  /**
   * Initialize the service with credentials from the current session
   * This should be called after user authentication
   */
  async initialize(): Promise<void> {
    try {
      // Get configuration from amplify_outputs.json
      const amplifyConfig = await import('../../amplify_outputs.json')
      this.region = amplifyConfig.auth.aws_region
      this.userPoolId = amplifyConfig.auth.user_pool_id

      console.log(`Initializing UserManagementService with region: ${this.region}, userPoolId: ${this.userPoolId}`)

      const session = await fetchAuthSession()
      const credentials = session.credentials

      if (!credentials) {
        throw new Error('No credentials available')
      }

      // Reinitialize clients with authenticated credentials and correct region
      this.cognitoClient = new CognitoIdentityProviderClient({
        region: this.region,
        credentials
      })

      this.iotClient = new IoTDataPlaneClient({
        region: this.region,
        credentials
      })

      console.log('UserManagementService initialized successfully')
    } catch (error) {
      console.error('Failed to initialize UserManagementService:', error)
      throw error
    }
  }

  /**
   * Create a new user in Cognito and sync to devices
   * 
   * @param request - User creation request with credentials and group assignment
   * @returns Promise<string> - The created user's ID (username)
   * @throws Error if user creation fails
   */
  async createUser(request: CreateUserRequest): Promise<string> {
    try {
      console.log(`Creating user: ${request.username} in group: ${request.cognitoGroup}`)

      // 1. Create user in Cognito
      const createUserCommand = new AdminCreateUserCommand({
        UserPoolId: this.userPoolId,
        Username: request.username,
        UserAttributes: [
          { Name: 'email', Value: request.email },
          { Name: 'email_verified', Value: 'true' },
          { Name: 'custom:parent_group', Value: request.parentGroup },
          { Name: 'custom:temp_password', Value: request.temporaryPassword } // Store for device sync
        ],
        TemporaryPassword: request.temporaryPassword,
        MessageAction: MessageActionType.SUPPRESS // Don't send email, we'll handle it
      })

      const createResult = await this.cognitoClient.send(createUserCommand)
      const userId = createResult.User?.Username

      if (!userId) {
        throw new Error('Failed to create user - no user ID returned')
      }

      console.log(`User created in Cognito: ${userId}`)

      // 2. Add user to Cognito group
      const addToGroupCommand = new AdminAddUserToGroupCommand({
        UserPoolId: this.userPoolId,
        Username: userId,
        GroupName: request.cognitoGroup
      })

      await this.cognitoClient.send(addToGroupCommand)
      console.log(`User added to group: ${request.cognitoGroup}`)

      // 3. Set permanent password (optional - makes password permanent instead of temporary)
      const setPasswordCommand = new AdminSetUserPasswordCommand({
        UserPoolId: this.userPoolId,
        Username: userId,
        Password: request.temporaryPassword,
        Permanent: true
      })

      await this.cognitoClient.send(setPasswordCommand)
      console.log('User password set as permanent')

      // 4. Hash password for device storage
      const passwordHash = await this.hashPasswordForDevice(request.temporaryPassword)

      // 5. Sync user to devices via MQTT
      const userData: UserData = {
        username: request.username,
        email: request.email,
        passwordHash,
        cognitoGroup: request.cognitoGroup,
        parentGroup: request.parentGroup,
        isActive: true
      }

      await this.syncUserToDevices(userData, request.deviceIds)
      console.log('User synced to devices')

      return userId
    } catch (error) {
      console.error('Error creating user:', error)
      throw new Error(`Failed to create user: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  /**
   * Delete a user from Cognito and deactivate on devices
   * 
   * @param username - Username to delete
   * @param deviceIds - Optional: specific devices to sync deletion to
   * @throws Error if user deletion fails
   */
  async deleteUser(username: string, deviceIds?: string[]): Promise<void> {
    try {
      console.log(`Deleting user: ${username}`)

      // 1. Get user details before deletion (for parent group info)
      let parentGroup = ''
      try {
        // We'll need the parent group to determine which devices to sync to
        // This is a simplified approach - in production, you might want to query user attributes
        parentGroup = '' // Will be determined from deviceIds or all devices
      } catch (error) {
        console.warn('Could not retrieve user parent group:', error)
      }

      // 2. Delete user from Cognito
      const deleteUserCommand = new AdminDeleteUserCommand({
        UserPoolId: this.userPoolId,
        Username: username
      })

      await this.cognitoClient.send(deleteUserCommand)
      console.log(`User deleted from Cognito: ${username}`)

      // 3. Publish user deactivation to devices
      const userData: UserData = {
        username,
        email: '', // Not needed for deletion
        passwordHash: '', // Not needed for deletion
        cognitoGroup: 'groupUser', // Not needed for deletion
        parentGroup,
        isActive: false // Mark as inactive
      }

      await this.syncUserToDevices(userData, deviceIds)
      console.log('User deactivation synced to devices')
    } catch (error) {
      console.error('Error deleting user:', error)
      throw new Error(`Failed to delete user: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  /**
   * Sync user data to devices via MQTT
   * 
   * @param userData - User data to sync
   * @param deviceIds - Optional: specific devices to sync to. If not provided, syncs to all devices in parent group
   */
  private async syncUserToDevices(userData: UserData, deviceIds?: string[]): Promise<void> {
    try {
      // Determine message type based on user status
      const messageType: SyncMessageType = userData.isActive ? 'IncrementalSync' : 'UserDeleted'

      // Create sync message
      const message: UserSyncMessage = {
        messageType,
        users: [userData],
        timestamp: new Date().toISOString()
      }

      let targetDevices: Device[] = []

      if (deviceIds && deviceIds.length > 0) {
        // Sync to specific devices
        console.log(`Syncing user to ${deviceIds.length} specific devices`)
        
        // Get device details for the specified device IDs
        for (const deviceId of deviceIds) {
          try {
            const { data: devices } = await dataClient.models.Device.listDeviceByDeviceId({
              deviceId
            })
            
            if (devices && devices.length > 0) {
              targetDevices.push({
                id: devices[0].id,
                deviceId: devices[0].deviceId,
                userId: devices[0].userId,
                iotThingName: devices[0].iotThingName || undefined
              })
            }
          } catch (error) {
            console.warn(`Could not find device ${deviceId}:`, error)
          }
        }
      } else if (userData.parentGroup) {
        // Sync to all devices in parent group
        console.log(`Syncing user to all devices in group: ${userData.parentGroup}`)
        targetDevices = await this.getDevicesForGroup(userData.parentGroup)
      } else {
        console.warn('No device IDs or parent group specified - skipping device sync')
        return
      }

      // Publish to each device
      console.log(`Publishing user sync to ${targetDevices.length} devices`)
      const publishPromises = targetDevices.map(device => 
        this.publishToDevice(device.deviceId, message)
      )

      await Promise.allSettled(publishPromises)
      
      const successCount = (await Promise.allSettled(publishPromises))
        .filter(result => result.status === 'fulfilled').length
      
      console.log(`User sync published to ${successCount}/${targetDevices.length} devices`)
    } catch (error) {
      console.error('Error syncing user to devices:', error)
      throw new Error(`Failed to sync user to devices: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  /**
   * Publish user sync message to a specific device via MQTT
   * 
   * @param deviceId - Device ID to publish to
   * @param message - User sync message to publish
   */
  private async publishToDevice(deviceId: string, message: UserSyncMessage): Promise<void> {
    try {
      // MQTT topic format: devices/{device_id}/users/sync
      const topic = `devices/${deviceId}/users/sync`

      // Create publish command
      const command = new PublishCommand({
        topic,
        payload: Buffer.from(JSON.stringify(message)),
        qos: 1 // At least once delivery
      })

      // Publish message
      await this.iotClient.send(command)
      console.log(`Published user sync to device ${deviceId} on topic ${topic}`)
    } catch (error) {
      console.error(`Error publishing to device ${deviceId}:`, error)
      // Don't throw - we want to continue publishing to other devices
      // The error will be logged and the Promise.allSettled will track failures
    }
  }

  /**
   * Get all devices for a specific parent group (organization)
   * 
   * @param parentGroup - Parent group identifier
   * @returns Promise<Device[]> - Array of devices in the group
   */
  private async getDevicesForGroup(parentGroup: string): Promise<Device[]> {
    try {
      console.log(`Querying devices for parent group: ${parentGroup}`)

      // Get current user's session to determine their userId
      const session = await fetchAuthSession()
      const userId = session.userSub

      if (!userId) {
        throw new Error('User not authenticated')
      }

      // Query devices by userId
      // Note: In a production system, you might want to add a parentGroup field to the Device model
      // For now, we'll get all devices for the user and filter by organization
      const { data: devices, errors } = await dataClient.models.Device.listDeviceByUserIdAndCreatedAt({
        userId
      })

      if (errors) {
        console.error('Error querying devices:', errors)
        throw new Error('Failed to query devices')
      }

      // Map to Device interface
      const deviceList: Device[] = devices.map(device => ({
        id: device.id,
        deviceId: device.deviceId,
        userId: device.userId,
        iotThingName: device.iotThingName || undefined
      }))

      console.log(`Found ${deviceList.length} devices for group ${parentGroup}`)
      return deviceList
    } catch (error) {
      console.error('Error getting devices for group:', error)
      throw new Error(`Failed to get devices for group: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  /**
   * List all users in the Cognito User Pool
   * 
   * @returns Promise<Array> - Array of users with their attributes
   * @throws Error if listing users fails
   */
  async listUsers(): Promise<Array<{
    username: string
    email: string
    cognitoGroup: string
    parentGroup: string
    status: string
    createdAt: string
    enabled: boolean
  }>> {
    try {
      console.log('Listing users from Cognito')

      const { ListUsersCommand, AdminListGroupsForUserCommand } = await import('@aws-sdk/client-cognito-identity-provider')

      // List all users in the user pool
      const listUsersCommand = new ListUsersCommand({
        UserPoolId: this.userPoolId,
        Limit: 60 // Maximum allowed by Cognito
      })

      const listUsersResult = await this.cognitoClient.send(listUsersCommand)
      
      if (!listUsersResult.Users || listUsersResult.Users.length === 0) {
        console.log('No users found')
        return []
      }

      console.log(`Found ${listUsersResult.Users.length} users`)

      // Get group membership for each user
      const usersWithGroups = await Promise.all(
        listUsersResult.Users.map(async (user) => {
          const username = user.Username || ''
          
          // Get user's groups
          let cognitoGroup = 'groupUser' // Default
          try {
            const listGroupsCommand = new AdminListGroupsForUserCommand({
              UserPoolId: this.userPoolId,
              Username: username
            })
            const groupsResult = await this.cognitoClient.send(listGroupsCommand)
            
            // Get the first group (users should only be in one group)
            if (groupsResult.Groups && groupsResult.Groups.length > 0) {
              cognitoGroup = groupsResult.Groups[0].GroupName || 'groupUser'
            }
          } catch (error) {
            console.warn(`Could not get groups for user ${username}:`, error)
          }

          // Extract user attributes
          const attributes = user.Attributes || []
          const email = attributes.find(attr => attr.Name === 'email')?.Value || ''
          const parentGroup = attributes.find(attr => attr.Name === 'custom:parent_group')?.Value || ''

          return {
            username,
            email,
            cognitoGroup,
            parentGroup,
            status: user.UserStatus || 'UNKNOWN',
            createdAt: user.UserCreateDate?.toISOString() || new Date().toISOString(),
            enabled: user.Enabled !== false
          }
        })
      )

      console.log(`Retrieved ${usersWithGroups.length} users with group information`)
      return usersWithGroups
    } catch (error) {
      console.error('Error listing users:', error)
      throw new Error(`Failed to list users: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  /**
   * Hash password for device storage using Argon2
   * 
   * Calls the password hashing Lambda function that uses the same Argon2 parameters
   * as the device's security module.
   * 
   * @param password - Plain text password to hash
   * @returns Promise<string> - Argon2 password hash
   * @throws Error if password hashing fails
   */
  private async hashPasswordForDevice(password: string): Promise<string> {
    try {
      console.log('Hashing password for device storage')

      // Validate password before sending to Lambda
      if (!password || password.length < 8) {
        throw new Error('Password must be at least 8 characters long')
      }

      if (password.length > 128) {
        throw new Error('Password must be less than 128 characters long')
      }

      // Call the hashPassword mutation via Amplify Data client
      const { data, errors } = await dataClient.mutations.hashPassword({
        password
      })

      console.log('GraphQL response - data:', JSON.stringify(data, null, 2))
      console.log('GraphQL response - errors:', errors)

      if (errors) {
        console.error('GraphQL errors hashing password:', errors)
        const errorMessage = errors.map(e => e.message).join(', ')
        throw new Error(`Failed to hash password: ${errorMessage}`)
      }

      if (!data) {
        throw new Error('No data returned from password hashing Lambda')
      }

      // Parse the response - AppSync returns JSON as a string
      let response: { success: boolean; passwordHash?: string; error?: string }
      
      if (typeof data === 'string') {
        response = JSON.parse(data)
      } else {
        response = data as unknown as { success: boolean; passwordHash?: string; error?: string }
      }

      console.log('Parsed response:', response)

      if (!response.success) {
        const errorMsg = response.error || 'Password hashing failed'
        console.error('Password hashing Lambda returned error:', errorMsg)
        throw new Error(errorMsg)
      }

      if (!response.passwordHash) {
        throw new Error('No password hash returned from Lambda')
      }

      // Verify hash format (should start with $argon2id$)
      if (!response.passwordHash.startsWith('$argon2id$')) {
        console.error('Invalid hash format returned:', response.passwordHash.substring(0, 20))
        throw new Error('Invalid password hash format returned from Lambda')
      }

      console.log('Password hashed successfully')
      return response.passwordHash
    } catch (error) {
      console.error('Error calling password hashing Lambda:', error)
      
      // Re-throw with more context
      if (error instanceof Error) {
        throw new Error(`Failed to hash password: ${error.message}`)
      }
      
      throw new Error('Failed to hash password: Unknown error')
    }
  }
  
  /**
   * Get current authenticated user
   */
  async getCurrentUser() {
    const { getCurrentUser } = await import('aws-amplify/auth')
    return await getCurrentUser()
  }
}

// Export singleton instance
export const userManagementService = new UserManagementService()
export default userManagementService
